import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import { reportError } from "../../utils/errorUtils";
import { SEARCH_EXTERNAL_PROVIDERS } from "../../queries/screener";
import { SelectedProvider } from "../../pages/Screener";
import { Provider, findSelectedProvider, selectProvider, toOptions } from "./providerOptions";

const SEARCH_DEBOUNCE_MS = 300;

type PediatricianPickerProps = {
  readonly value: SelectedProvider | null;
  readonly onChange: (provider: SelectedProvider | null) => void;
};

export default function PediatricianPicker({ value, onChange }: PediatricianPickerProps) {
  const [inputValue, setInputValue] = useState(value?.providerName ?? "");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, loading, refetch } = useQuery(SEARCH_EXTERNAL_PROVIDERS, {
    variables: { searchText: "" },
    fetchPolicy: "no-cache",
    onError: reportError
  });

  const providers: readonly Provider[] = data?.searchExternalProviders ?? [];
  const options = useMemo(() => toOptions(providers), [providers]);

  const debouncedSearch = useDebouncedSearch((searchText: string) => {
    refetch({ searchText });
  });

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setInputValue(next);
      setOpen(true);
      onChange(null);
      debouncedSearch(next.trim());
    },
    [debouncedSearch, onChange]
  );

  const handleSelect = useCallback(
    (optionValue: string) => {
      const provider = findSelectedProvider(providers, optionValue);
      if (!provider) return;
      const chosen = selectProvider(provider);
      setInputValue(chosen.providerName);
      setOpen(false);
      onChange(chosen);
    },
    [providers, onChange]
  );

  return (
    <div className="screener-field">
      <label className="screener-field__label" htmlFor="screener-pediatrician">
        Pediatrician
      </label>
      <div className="screener-combobox" ref={containerRef}>
        <input
          id="screener-pediatrician"
          className="screener-combobox__input"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="screener-pediatrician-list"
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Type or select a provider"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
        />
        <span className="screener-combobox__caret" aria-hidden="true">
          <CaretIcon />
        </span>
        {open && (
          <ul id="screener-pediatrician-list" className="screener-combobox__list" role="listbox">
            {loading && <li className="screener-combobox__status">Loading…</li>}
            {!loading && options.length === 0 && <li className="screener-combobox__status">No matches</li>}
            {!loading &&
              options.map(option => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={value?.externalProviderId === Number(option.value)}
                >
                  <button
                    type="button"
                    className="screener-combobox__option-button"
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CaretIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" focusable="false">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function useDebouncedSearch(run: (searchText: string) => void) {
  const runRef = useRef(run);
  runRef.current = run;

  const debounced = useMemo(
    () => debounce((searchText: string) => runRef.current(searchText), SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
}
